import { useMantineColorScheme, useMantineTheme } from "@mantine/core";

/**
 * Hook to easily access Mantine theme colors.
 * Provides a convenient way to get color values from the theme.
 *
 * @example
 * ```tsx
 * const colors = useMantineColors();
 * 
 * const gray6 = colors.gray[6];
 * const primaryColor = colors.primary;
 * 
 * const euFlag = colors.eu_flag[6];
 * 
 * <div style={{ color: colors.gray[6] }}>Text</div>
 * ```
 */
export function useMantineColors() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const getPrimaryColor = () => {
    const primaryColorName = theme.primaryColor;
    
    let primaryShade: number;
    if (typeof theme.primaryShade === "number") {
      primaryShade = theme.primaryShade;
    } else if (theme.primaryShade && typeof theme.primaryShade === "object") {
      primaryShade = colorScheme === "dark" 
        ? theme.primaryShade.dark 
        : theme.primaryShade.light;
    } else {
      primaryShade = 6;
    }
    
    const primaryColorArray = theme.colors[primaryColorName as keyof typeof theme.colors];
    return primaryColorArray?.[primaryShade] || theme.colors.blue[6];
  };

  return {
    gray: theme.colors.gray,
    dark: theme.colors.dark,
    red: theme.colors.red,
    pink: theme.colors.pink,
    grape: theme.colors.grape,
    violet: theme.colors.violet,
    indigo: theme.colors.indigo,
    blue: theme.colors.blue,
    cyan: theme.colors.cyan,
    teal: theme.colors.teal,
    green: theme.colors.green,
    lime: theme.colors.lime,
    yellow: theme.colors.yellow,
    orange: theme.colors.orange,

    eu_flag: theme.colors.eu_flag,
    light_azure: theme.colors.light_azure,
    eu_gold: theme.colors.eu_gold,
    modern_teal: theme.colors.modern_teal,
    charcoal_gray: theme.colors.charcoal_gray,
    dark_gray: theme.colors.dark_gray,
    soft_gray: theme.colors.soft_gray,
    white: theme.colors.white,
    border: theme.colors.border,
    danger: theme.colors.danger,
    background: theme.colors.background,

    primary: getPrimaryColor(),

    getColor: (colorName: string, shade: number = 6) => {
      const color = theme.colors[colorName as keyof typeof theme.colors];
      return color?.[shade] || theme.colors.gray[shade];
    },

    theme,
  };
}

